import Tag from 'components/common/tag';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'components/layout/popover';
import {
  AccessoryProductType,
  AccessoryWithQuantityType,
  AirHandlerProductType,
  CondenserProductType,
  DuctedSystemProductType
} from 'data/types';
import { abbreviateNumber } from 'utils/helpers';
import AirHandlerDiagram from './air-handler-diagram';
import CondenserDiagram from './condenser-diagram';

type ProductLinkProps =
  | {
      type: 'air handler';
      product: AirHandlerProductType;
      quantity?: number;
      fullName?: boolean;
      hoverInfo?: boolean;
    }
  | {
      type: 'condenser';
      product: CondenserProductType;
      quantity?: number;
      fullName?: boolean;
      hoverInfo?: boolean;
    }
  | {
      type: 'ducted product';
      product: DuctedSystemProductType;
      quantity?: number;
      fullName?: boolean;
      hoverInfo?: boolean;
    }
  | {
      type: 'accessory';
      product: AccessoryProductType | AccessoryWithQuantityType;
      quantity?: number;
      fullName?: boolean;
      hoverInfo?: boolean;
    };

export default function ProductLink({
  type,
  product,
  quantity = 1,
  fullName,
  hoverInfo
}: ProductLinkProps) {
  return (
    <Popover placement='bottom-start' hoverEnabled>
      <div>
        <PopoverTrigger asChild>
          <a
            className='font-semibold text-blue-500 hover:text-blue-600'
            href={`https://alpinehomeair.com${product.productUrl}`}
            rel='noreferrer'
            target='_blank'
            onClick={(e) => e.stopPropagation()}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {type === 'accessory'
              ? `${product.name}${quantity !== undefined && ` x ${quantity}`}`
              : fullName
              ? product.name
              : product.model}
          </a>
        </PopoverTrigger>
      </div>
      {hoverInfo && (
        <PopoverContent className='z-30 flex w-[400px] flex-col gap-2 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5 focus:outline-none'>
          {type === 'condenser' && (
            <>
              <div className='flex flex-wrap gap-2'>
                <span className='font-semibold'>{product.name}</span>
                <Tag
                  text={`${abbreviateNumber(
                    Math.round(product.coolingBTU / 1000) * 1000
                  )} BTU`}
                />
                <Tag text={`${product.seer} SEER`} />
              </div>
              <div className='text-sm text-gray-600'>{product.description}</div>
              <ul className='mb-2 list-disc'>
                {product.requirements.map((req) => (
                  <li key={req.value} className='ml-5'>
                    {req.value}
                  </li>
                ))}
              </ul>
              <CondenserDiagram product={product} />
            </>
          )}
          {type === 'air handler' && (
            <>
              <div className='flex flex-wrap gap-2'>
                <span className='font-semibold'>{product.name}</span>
                <Tag text={`${product.sizeCode.toUpperCase()} BTU`} />
              </div>
              <div className='text-sm text-gray-600'>{product.description}</div>
              <ul className='mb-2 list-disc'>
                {product.requirements.map((req) => (
                  <li key={req.value} className='ml-5'>
                    {req.value}
                  </li>
                ))}
              </ul>
              <AirHandlerDiagram product={product} />
            </>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
}
